// LRPO-5180 — full-year tooltip titles for the year-less "Leads Per Day"
// day charts on the Reports Hub Lead Activity + Overview tabs.
//
// The day charts render with `discrete: true` so Chartkick treats the
// x-axis as a string/category axis: the ticks stay the readable, year-less
// "Jun 17" labels (from Ruby strftime '%b %d') and Chart.js never re-parses
// them as dates (the old datetime path ran Date.parse("Jun 17") → V8's 2001
// default → "PP" tooltip "Jun 17, 2001"). To still show the REAL year in the
// tooltip, this factory returns a Chart.js tooltip `title` callback that
// looks the clicked category label up in a server-supplied label→full-date
// map ("Jun 17" → "Jun 17, 2026"), falling back to the raw label.
//
// Chartkick's Ruby `library:` option is JSON-only (no JS functions), so this
// callback is wired onto the chart in JS after creation — the same place the
// drilldown `onClick` is patched — not in the ERB library hash.

// Returns a Chart.js tooltip-title callback. `labelToFull` maps each
// year-less category label to its real-year full-date string. The returned
// function is null-safe: an empty items array yields "", and an unmapped or
// undefined label degrades to the raw label rather than blanking the title.
export function dayTooltipTitle(labelToFull) {
  const map = labelToFull || {}
  return function (items) {
    if (!items || !items.length) return ''
    const label = items[0].label
    if (label == null) return ''
    return map[label] != null ? map[label] : label
  }
}

// Wires the real-year tooltip-title callback onto one discrete Chartkick day
// chart. `chartId` is the Chartkick chart id; `labelMap` is the year-less
// label → full-date map (or, in multi-year mode, label → itself). Sets (i.e.
// replaces) `options.plugins.tooltip.callbacks.title` while PRESERVING every
// other tooltip option/callback. Returns false (a safe no-op) when Chartkick,
// the chart, the underlying Chart.js object, or the factory isn't ready —
// callers OR the result with their other wire() results to decide whether to
// retry on `chartkick:load`.
//
// LRPO-5198 — the way we walk down to `callbacks.title` matters: `cjs.options`
// is a Chart.js v4 resolver Proxy whose intermediate sub-objects already exist
// as proxies, so the tempting `x.y = x.y || {}` self-default idiom assigns a
// proxy back into its own slot and makes `cjs.update()` recurse forever
// ("Maximum call stack size exceeded"). We therefore ONLY create an
// intermediate when it is actually absent and set just the leaf. (See the
// inline comment at the assignment below.)
//
// Extracted from the byte-identical inline copies in the Lead Activity and
// Overview drilldown <script>s (LRPO-5180 FIX 2 dedup). This is the SHARED
// helper used by BOTH the Overview and Lead Activity day charts via
// window.reportsWireDayTooltip, so the LRPO-5198 fix covers both surfaces.
export function wireDayTooltip(chartId, labelMap) {
  if (typeof window === 'undefined') return false
  if (typeof dayTooltipTitle !== 'function') return false
  if (!window.Chartkick) return false
  var ck = window.Chartkick.charts[chartId]
  if (!ck || typeof ck.getChartObject !== 'function') return false
  var cjs = ck.getChartObject()
  if (!cjs) return false

  // LRPO-5198 — DO NOT use the `x.y = x.y || {}` self-default idiom here.
  // In Chart.js v4 `cjs.options` is a resolver Proxy whose `plugins`,
  // `tooltip`, and `callbacks` sub-objects already resolve to truthy
  // proxies by default. `x.y = x.y || {}` short-circuits to the EXISTING
  // proxy and assigns it back into its own slot — storing a proxy as a raw
  // value inside the options tree. On `cjs.update()` the resolver re-wraps
  // that proxy-in-proxy and two Proxy `set` traps recurse infinitely →
  // "RangeError: Maximum call stack size exceeded" on the Reports page.
  // So only CREATE an intermediate when it is GENUINELY missing (never
  // reassign one that exists), and set just the leaf `.title` callback.
  const opts = cjs.options
  if (!opts.plugins) opts.plugins = {}
  if (!opts.plugins.tooltip) opts.plugins.tooltip = {}
  if (!opts.plugins.tooltip.callbacks) opts.plugins.tooltip.callbacks = {}
  opts.plugins.tooltip.callbacks.title = dayTooltipTitle(labelMap || {})
  cjs.update('none')
  return true
}

// ---------------------------------------------------------------------------
// LRPO-5272 — external (DOM) tooltip for the two multi-series "by Activation"
// charts on the Lead Activity tab: "Leads Per Day by Activation" (stacked
// area) and "Leads Per Hour by Activation" (grouped column).
//
// Both use `tooltip: { mode: 'index', intersect: false }`, so each series is
// one activation and a single hover lists EVERY activation. Chart.js paints
// the native tooltip onto the <canvas>, which is fixed at height:420px
// (maintainAspectRatio:false). With many activations the tooltip grows past
// 420px and is CLIPPED to the canvas bounds — the lower activations are
// unreachable. The fix disables the native tooltip and renders the same
// per-activation list into an HTML <div> OUTSIDE the canvas (appended to the
// chart card body), with `max-height` + `overflow-y:auto` so every activation
// is rendered and reachable, positioned near the cursor but clamped to the
// viewport.
// ---------------------------------------------------------------------------

const ACTIVATION_TOOLTIP_CLASS = 'reports-activation-tooltip'
const ACTIVATION_TOOLTIP_PREFIX = 'reports-activation-tooltip-'

// Minimal HTML escaping — activation names are user-supplied (the named
// scanners the exhibitor created), so escape before injecting via innerHTML.
function escapeActivationHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

// Where the tooltip <div> lives: the chart's own card body (so it is scoped
// to the card and torn down with it), falling back to <body> when the canvas
// isn't attached yet (e.g. jsdom / pre-render).
function activationTooltipParent(chart) {
  const canvas = chart && chart.canvas
  if (canvas && typeof canvas.closest === 'function') {
    const body = canvas.closest('.report-chart-card__body')
    if (body) return body
  }
  return typeof document !== 'undefined' && document.body ? document.body : null
}

// Base, build-step-free inline styling. `position:fixed` keeps the box in
// viewport coordinates (trivial to clamp) regardless of which ancestor it is
// appended to. The crux of the fix: the full activation list is always in the
// DOM and the box is position:fixed, so it grows OUTSIDE the 420px canvas
// instead of being clipped to it (the original bug). max-height is the viewport
// (minus a small margin) so even a long activation list is shown in full —
// LRPO-5272's "display all activations".
//
// LRPO-5272 rework — QA found that at some browser zoom levels the list still
// grows past max-height with no way to reach the rest: `pointer-events:none`
// (the conventional Chart.js external-tooltip choice, to keep a hovering box
// from fighting the canvas) also means the browser never routes a wheel event
// or scrollbar drag to this element, so overflow-y:auto never actually
// engages for the user — it's there, just unreachable. `pointer-events:auto`
// (same "re-enable on the interactive card itself" idiom as
// .live-tour-tooltip in _live_tour.scss) fixes that; the flicker risk it
// re-introduces — moving off the <canvas> onto the tooltip trips a real
// `mouseout` on the canvas, which Chart.js reads as "hide" — is handled by
// the hover-lock listeners in bindActivationTooltipHoverLock() below, wired
// once at element creation.
function applyActivationTooltipBaseStyle(el) {
  const s = el.style
  s.position = 'fixed'
  s.zIndex = '1080'
  s.pointerEvents = 'auto'
  s.maxHeight = 'calc(100vh - 24px)'
  s.overflowY = 'auto'
  s.maxWidth = '320px'
  s.background = 'rgba(17, 24, 39, 0.95)'
  s.color = '#fff'
  s.borderRadius = '6px'
  s.padding = '8px 10px'
  s.font = '12px/1.45 system-ui, -apple-system, sans-serif'
  s.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.25)'
  s.opacity = '0'
  s.transition = 'opacity 0.1s ease'
}

// LRPO-5272 rework — companion to the pointer-events:auto flip above. The
// tooltip <div> is a DOM sibling of the <canvas>, not a descendant, so moving
// the cursor from a data point onto the (now interactive) tooltip to scroll
// it makes the browser fire a genuine 'mouseout' on the canvas; Chart.js
// treats that as "nothing is hovered" and calls the external handler with
// tooltip.opacity:0. Without this lock that hides the box the instant the
// user's cursor reaches it. `mouseenter`/`mouseleave` here just record
// whether the pointer is over the box; makeActivationTooltipHandler's
// opacity:0 branch checks the flag and skips hiding while it's set, and
// `mouseleave` hides the box directly once the pointer actually leaves it
// (Chart.js has no further reason to call the handler once the cursor is off
// both the canvas and the tooltip). Bound once, at element creation.
function bindActivationTooltipHoverLock(el) {
  el.addEventListener('mouseenter', function () {
    el.dataset.hovering = 'true'
  })
  el.addEventListener('mouseleave', function () {
    delete el.dataset.hovering
    el.style.opacity = '0'
  })
}

// Find (or lazily create) the id-scoped tooltip <div> for `chartId`.
function activationTooltipEl(chartId, chart) {
  if (typeof document === 'undefined') return null
  const elId = ACTIVATION_TOOLTIP_PREFIX + chartId
  let el = document.getElementById(elId)
  if (!el) {
    el = document.createElement('div')
    el.id = elId
    el.className = ACTIVATION_TOOLTIP_CLASS
    el.setAttribute('role', 'tooltip')
    applyActivationTooltipBaseStyle(el)
    bindActivationTooltipHoverLock(el)
    const parent = activationTooltipParent(chart)
    if (parent) parent.appendChild(el)
  }
  return el
}

// Build the tooltip body: the title (e.g. the real-year full date supplied by
// the LRPO-5180 day title callback, which Chart.js still runs even with the
// native tooltip disabled) followed by ONE row per dataPoint — color swatch,
// activation name, value. Lists every item; nothing is dropped for height.
function activationTooltipHtml(tooltip) {
  const points = (tooltip && tooltip.dataPoints) || []
  const labelColors = (tooltip && tooltip.labelColors) || []
  let html = ''

  const titleLines = (tooltip && tooltip.title) || []
  if (titleLines.length) {
    html +=
      '<div class="reports-activation-tooltip__title" style="font-weight:600;margin-bottom:4px;">' +
      titleLines.map(escapeActivationHtml).join('<br>') +
      '</div>'
  }

  html += '<div class="reports-activation-tooltip__list">'
  points.forEach(function (point, i) {
    const colors = labelColors[i] || {}
    const swatch = colors.backgroundColor || colors.borderColor || 'transparent'
    const name = point && point.dataset ? point.dataset.label : ''
    const value = point && point.formattedValue != null ? point.formattedValue : ''
    html +=
      '<div class="reports-activation-tooltip__row" style="display:flex;align-items:center;gap:6px;padding:1px 0;">' +
      '<span style="display:inline-block;width:10px;height:10px;border-radius:2px;flex:0 0 auto;background:' +
      escapeActivationHtml(swatch) +
      ';"></span>' +
      '<span style="flex:1 1 auto;white-space:nowrap;">' +
      escapeActivationHtml(name) +
      '</span>' +
      '<span style="font-weight:600;margin-left:8px;">' +
      escapeActivationHtml(value) +
      '</span>' +
      '</div>'
  })
  html += '</div>'
  return html
}

// Place the box near the caret (canvas-relative caretX/caretY mapped to
// viewport via the canvas rect) but clamp it inside the viewport so it is
// never cut off — the whole point of moving off the clipped canvas.
function positionActivationTooltip(el, chart, tooltip) {
  const canvas = chart && chart.canvas
  const rect =
    canvas && typeof canvas.getBoundingClientRect === 'function'
      ? canvas.getBoundingClientRect()
      : { left: 0, top: 0 }
  let left = rect.left + (tooltip.caretX || 0) + 12
  let top = rect.top + (tooltip.caretY || 0) + 12

  const w = el.offsetWidth || 0
  const h = el.offsetHeight || 0
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0
  if (vw) {
    if (left + w > vw - 8) left = vw - w - 8
    if (left < 8) left = 8
  }
  if (vh) {
    if (top + h > vh - 8) top = vh - h - 8
    if (top < 8) top = 8
  }
  el.style.left = left + 'px'
  el.style.top = top + 'px'
}

// Returns the Chart.js `external` tooltip handler for one chart. Chart.js
// invokes it with `{ chart, tooltip }`; the tooltip model is fully built
// (title via any callbacks, body, dataPoints, labelColors) even though the
// native tooltip is disabled — so we just render/position/show or hide.
export function makeActivationTooltipHandler(chartId) {
  return function (context) {
    const chart = context && context.chart
    const tooltip = context && context.tooltip
    const el = activationTooltipEl(chartId, chart)
    if (!el) return
    if (!tooltip || tooltip.opacity === 0) {
      // LRPO-5272 rework — don't hide out from under an active scroll. See
      // bindActivationTooltipHoverLock: leaving the canvas for the tooltip
      // reports opacity:0 here even while the user is still using it.
      if (el.dataset.hovering === 'true') return
      el.style.opacity = '0'
      return
    }
    el.innerHTML = activationTooltipHtml(tooltip)
    positionActivationTooltip(el, chart, tooltip)
    el.style.opacity = '1'
  }
}

// Tear the tooltip <div>s down before Turbo caches the page, so a stale,
// possibly-visible box is never serialized into the snapshot (they are
// recreated lazily on the next hover). Registered once per page load.
let activationTooltipCleanupBound = false
export function ensureActivationTooltipCleanup() {
  if (activationTooltipCleanupBound) return
  if (typeof document === 'undefined' || !document.addEventListener) return
  activationTooltipCleanupBound = true
  document.addEventListener('turbo:before-cache', function () {
    const nodes = document.querySelectorAll('.' + ACTIVATION_TOOLTIP_CLASS)
    Array.prototype.forEach.call(nodes, function (n) {
      if (n.parentNode) n.parentNode.removeChild(n)
    })
  })
}

// Wires the external DOM tooltip onto one multi-series "by Activation"
// Chartkick chart: disables the native (clipped) tooltip and installs the
// external renderer. Returns false (safe no-op) when Chartkick / the chart /
// the Chart.js object isn't ready — callers OR this with their other wire()
// results to decide whether to retry on `chartkick:load`.
//
// LRPO-5198 proxy guard — SAME hazard as wireDayTooltip: `cjs.options` is a
// Chart.js v4 resolver Proxy whose `plugins` and `tooltip` sub-objects already
// resolve to truthy proxies. The `x.y = x.y || {}` self-default idiom would
// assign a proxy back into its own slot and make `cjs.update()` recurse to
// "Maximum call stack size exceeded". So we ONLY create an intermediate when
// it is GENUINELY absent (never reassign an existing one) and write just the
// LEAF values `.enabled` (a boolean) and `.external` (a function) — never a
// proxy. The ERB-supplied `mode`/`intersect` leaves on the same tooltip object
// are preserved because we don't touch (or recreate) the tooltip object.
export function wireActivationTooltip(chartId) {
  if (typeof window === 'undefined') return false
  if (!window.Chartkick) return false
  var ck = window.Chartkick.charts[chartId]
  if (!ck || typeof ck.getChartObject !== 'function') return false
  var cjs = ck.getChartObject()
  if (!cjs) return false

  const opts = cjs.options
  if (!opts.plugins) opts.plugins = {}
  if (!opts.plugins.tooltip) opts.plugins.tooltip = {}
  // Leaf writes only (boolean + function) — never reassign the proxy itself.
  opts.plugins.tooltip.enabled = false
  opts.plugins.tooltip.external = makeActivationTooltipHandler(chartId)

  ensureActivationTooltipCleanup()
  cjs.update('none')
  return true
}

// Inline view <script> blocks cannot `import`, so expose the factories on
// `window` for them to reach (mirrors the window.leadDrill pattern in
// app/frontend/lead_drill.js).
if (typeof window !== 'undefined') {
  window.reportsDayTooltipTitle = dayTooltipTitle
  window.reportsWireDayTooltip = wireDayTooltip
  window.reportsWireActivationTooltip = wireActivationTooltip
}
